<template>
    <div class="pdf-viewer-wrapper">
        <div ref="scrollContainer" class="pdf-viewer" @scroll="handleScroll">
            <div v-for="pageNum in pagesToRender" :key="pageNum" class="pdf-viewer__page-container">
                <canvas :id="`pdf-page-${pageNum}`" class="pdf-viewer__page"></canvas>
                <div v-if="expired" class="pdf-viewer__expired-overlay">
                    <span class="pdf-viewer__expired-text typographies-paragraph-large-bold">
                        {{ translations.expired }}
                    </span>
                </div>
            </div>
        </div>
        <PDFViewerControls
            ref="controls"
            :totalPages="totalPages"
            :pdfSrc="src"
            :showZoom="showZoom"
            :showPagination="showPagination"
            :showExternalLink="showExternalLink"
            :visible="showControls"
            @scaleChanged="handleScaleChanged"
            @pageChanged="handlePageChanged"
        />
    </div>
</template>

<script>
import { markRaw } from 'vue';
import PDFViewerControls from 'vcomponents/common/pdf/PDFViewerControls.vue';

const PDFJS_CONFIG = {
    version: '3.11.174',
    get cdnUrl() {
        return `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${this.version}/pdf.min.js`;
    },
    get workerUrl() {
        return `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${this.version}/pdf.worker.min.js`;
    },
};
const ZOOM_CONFIG = {
    MIN: 10,
    MAX: 300,
    STEP: 10,
    DEFAULT: 100,
    DEBOUNCE_DELAY: 10,
};

export default {
    components: {
        PDFViewerControls,
    },
    props: {
        src: {
            type: String,
            required: true,
        },
        showZoom: {
            type: Boolean,
            default: true,
        },
        showPagination: {
            type: Boolean,
            default: true,
        },
        showExternalLink: {
            type: Boolean,
            default: true,
        },
        expired: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['pdf-loaded', 'pdf-error', 'page-rendered', 'render-error'],
    data() {
        return {
            translations: {
                expired: this.$t.__('common.labels.expired').toUpperCase(),
            },
            pdfjsLib: null,
            pdfJsLoadingPromise: null,
            pdf: null,
            totalPages: 0,
            isRendering: false,
            renderTasks: new Map(),
            pageCache: new Map(),
            isDragging: false,
            startX: 0,
            startY: 0,
            scrollLeft: 0,
            scrollTop: 0,
            showControls: false,
        };
    },
    computed: {
        pagesToRender() {
            return Array.from({ length: this.totalPages }, (_, i) => i + 1);
        },
    },
    watch: {
        src() {
            this.loadPdf();
        },
    },
    async mounted() {
        await this.loadPdfJs();
        this.loadPdf();
        this.setupZoomListener();
        this.setupScrollListener();
        this.setupDragListener();
        this.setupHoverListener();
    },
    beforeUnmount() {
        this.cancelAllRenderTasks();
        if (this.pdf) {
            this.pdf.destroy();
        }
        this.removeZoomListener();
        this.removeScrollListener();
        this.removeDragListener();
        this.removeHoverListener();
        this.pageCache.clear();
    },
    methods: {
        handleScaleChanged() {
            this.debouncedRerender();
        },
        handlePageChanged(pageNum) {
            this.scrollToPage(pageNum);
        },
        scrollToPage(pageNum) {
            if (pageNum < 1 || pageNum > this.totalPages) {
                return;
            }
            const canvas = document.getElementById(`pdf-page-${pageNum}`);
            if (!canvas || !this.$refs.scrollContainer) {
                return;
            }
            const container = this.$refs.scrollContainer;
            const pageContainer = canvas.parentElement;
            const containerTop = pageContainer.offsetTop;
            container.scrollTo({
                top: containerTop,
                behavior: 'smooth',
            });
        },
        async loadPdfJs() {
            if (this.pdfjsLib) {
                return;
            }
            if (this.pdfJsLoadingPromise) {
                return this.pdfJsLoadingPromise;
            }
            const script = document.createElement('script');
            script.src = PDFJS_CONFIG.cdnUrl;
            this.pdfJsLoadingPromise = new Promise((resolve, reject) => {
                script.onload = () => {
                    this.pdfjsLib = window.pdfjsLib;
                    this.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_CONFIG.workerUrl;
                    resolve();
                };
                script.onerror = (e) => {
                    this.pdfJsLoadingPromise = null;
                    reject(e);
                };
                document.head.appendChild(script);
            });
            return this.pdfJsLoadingPromise;
        },
        async loadPdf() {
            if (!this.src) return;

            try {
                const loadingTask = this.pdfjsLib.getDocument({
                    url: this.src,
                    renderInteractiveForms: true,
                });
                this.pdf = markRaw(await loadingTask.promise);
                this.totalPages = this.pdf.numPages;

                // Wait for DOM to be ready and painted
                await this.$nextTick();
                await new Promise((resolve) => requestAnimationFrame(resolve));

                for (const pageNum of this.pagesToRender) {
                    await this.renderPage(pageNum);
                }
                this.$emit('pdf-loaded', this.pdf);
            } catch (error) {
                this.$emit('pdf-error', error);
            }
        },
        async renderPage(pageNum = 1) {
            if (!this.pdf) return;

            this.cancelRenderTask(pageNum);
            try {
                let page = this.pageCache.get(pageNum);
                if (!page) {
                    page = markRaw(await this.pdf.getPage(pageNum));
                    this.pageCache.set(pageNum, page);
                }
                const canvas = document.getElementById(`pdf-page-${pageNum}`);
                if (!canvas) {
                    return;
                }
                let ctx = canvas._cachedContext;
                if (!ctx) {
                    ctx = canvas.getContext('2d', {
                        alpha: false,
                        desynchronized: true,
                    });
                    canvas._cachedContext = ctx;
                }
                const dpr = window.devicePixelRatio || 1;
                const containerWidth = this.$refs.scrollContainer?.clientWidth || window.innerWidth;
                const initialViewport = page.getViewport({ scale: 1 });
                const margin = 48;
                const baseScale = (containerWidth - margin) / initialViewport.width;
                const controlsScale = this.$refs.controls?.scale || 100;
                const scale = baseScale * (controlsScale / 100);
                const viewport = page.getViewport({ scale });
                const outputScale = dpr * 2;
                const canvasWidth = Math.floor(viewport.width * outputScale);
                const canvasHeight = Math.floor(viewport.height * outputScale);
                canvas.width = canvasWidth;
                canvas.height = canvasHeight;
                canvas.style.width = `${Math.floor(viewport.width)}px`;
                canvas.style.height = `${Math.floor(viewport.height)}px`;
                ctx.save();
                ctx.scale(outputScale, outputScale);
                const renderContext = {
                    canvasContext: ctx,
                    viewport,
                    enableWebGL: false,
                    renderInteractiveForms: true,
                };
                const renderTask = page.render(renderContext);
                this.renderTasks.set(pageNum, renderTask);
                await renderTask.promise;
                this.renderTasks.delete(pageNum);
                ctx.restore();
                this.$emit('page-rendered', { pageNum, viewport });
            } catch (error) {
                if (error.name !== 'RenderingCancelledException') {
                    this.$emit('render-error', error);
                }
            }
        },
        setupZoomListener() {
            this.handleWheel = (event) => {
                if (event.ctrlKey) {
                    event.preventDefault();
                    const delta = -event.deltaY;
                    const zoomFactor = delta > 0 ? 1 : -1;
                    const controls = this.$refs.controls;
                    if (!controls) {
                        return;
                    }
                    const newScale = Math.max(
                        controls.minZoom,
                        Math.min(controls.maxZoom, controls.scale + zoomFactor * 10),
                    );
                    if (newScale === controls.scale) {
                        return;
                    }
                    controls.scale = newScale;
                    this.debouncedRerender();
                }
            };
            this.$refs.scrollContainer?.addEventListener('wheel', this.handleWheel, {
                passive: false,
            });
        },
        removeZoomListener() {
            if (this.handleWheel) {
                this.$refs.scrollContainer?.removeEventListener('wheel', this.handleWheel);
            }
            if (this.renderTimeout) {
                clearTimeout(this.renderTimeout);
            }
        },
        debouncedRerender() {
            if (this.renderTimeout) {
                clearTimeout(this.renderTimeout);
            }
            this.renderTimeout = setTimeout(() => {
                this.rerenderAllPages();
            }, ZOOM_CONFIG.DEBOUNCE_DELAY);
        },
        async rerenderAllPages() {
            if (this.isRendering) {
                return;
            }
            this.isRendering = true;
            this.cancelAllRenderTasks();
            try {
                const renderPromises = [];
                for (const pageNum of this.pagesToRender) {
                    renderPromises.push(this.renderPage(pageNum));
                }
                await Promise.all(renderPromises);
            } finally {
                this.isRendering = false;
            }
        },
        cancelRenderTask(pageNum) {
            const task = this.renderTasks.get(pageNum);
            if (task) {
                task.cancel();
                this.renderTasks.delete(pageNum);
            }
        },
        cancelAllRenderTasks() {
            this.renderTasks.forEach((task) => {
                task.cancel();
            });
            this.renderTasks.clear();
        },
        handleScroll() {
            if (!this.$refs.scrollContainer || this.totalPages === 0) {
                return;
            }
            if (this.scrollTimeout) {
                return;
            }
            this.scrollTimeout = setTimeout(() => {
                this.scrollTimeout = null;
                const container = this.$refs.scrollContainer;
                const scrollTop = container.scrollTop;
                const containerHeight = container.clientHeight;
                const scrollMiddle = scrollTop + containerHeight / 2;
                for (const pageNum of this.pagesToRender) {
                    const canvas = document.getElementById(`pdf-page-${pageNum}`);
                    if (!canvas) {
                        continue;
                    }
                    const pageContainer = canvas.parentElement;
                    const containerTop = pageContainer.offsetTop;
                    const containerBottom = containerTop + pageContainer.offsetHeight;
                    if (scrollMiddle >= containerTop && scrollMiddle <= containerBottom) {
                        this.$refs.controls?.updateCurrentPage(pageNum);
                        break;
                    }
                }
            }, 50);
        },
        setupScrollListener() {
            this.$refs.scrollContainer?.addEventListener('scroll', this.handleScroll);
        },
        removeScrollListener() {
            this.$refs.scrollContainer?.removeEventListener('scroll', this.handleScroll);
        },
        setupDragListener() {
            const container = this.$refs.scrollContainer;
            if (!container) {
                return;
            }
            this.handleMouseDown = (e) => {
                this.isDragging = true;
                this.startX = e.pageX - container.offsetLeft;
                this.startY = e.pageY - container.offsetTop;
                this.scrollLeft = container.scrollLeft;
                this.scrollTop = container.scrollTop;
                container.style.cursor = 'grabbing';
                container.style.userSelect = 'none';
            };
            this.handleMouseLeave = () => {
                this.isDragging = false;
                container.style.cursor = 'grab';
            };
            this.handleMouseUp = () => {
                this.isDragging = false;
                container.style.cursor = 'grab';
            };
            this.handleMouseMove = (e) => {
                if (!this.isDragging) return;
                e.preventDefault();
                if (this.rafId) {
                    cancelAnimationFrame(this.rafId);
                }
                this.rafId = requestAnimationFrame(() => {
                    const x = e.pageX - container.offsetLeft;
                    const y = e.pageY - container.offsetTop;
                    const walkX = (x - this.startX) * 1.5;
                    const walkY = (y - this.startY) * 1.5;
                    container.scrollLeft = this.scrollLeft - walkX;
                    container.scrollTop = this.scrollTop - walkY;
                });
            };
            container.addEventListener('mousedown', this.handleMouseDown);
            container.addEventListener('mouseleave', this.handleMouseLeave);
            container.addEventListener('mouseup', this.handleMouseUp);
            container.addEventListener('mousemove', this.handleMouseMove);
            container.style.cursor = 'grab';
        },
        removeDragListener() {
            const container = this.$refs.scrollContainer;
            if (!container) {
                return;
            }
            if (this.rafId) {
                cancelAnimationFrame(this.rafId);
            }
            if (this.handleMouseDown) {
                container.removeEventListener('mousedown', this.handleMouseDown);
            }
            if (this.handleMouseLeave) {
                container.removeEventListener('mouseleave', this.handleMouseLeave);
            }
            if (this.handleMouseUp) {
                container.removeEventListener('mouseup', this.handleMouseUp);
            }
            if (this.handleMouseMove) {
                container.removeEventListener('mousemove', this.handleMouseMove);
            }
            container.style.cursor = '';
        },
        setupHoverListener() {
            const wrapper = this.$el;
            if (!wrapper) {
                return;
            }
            this.handleHoverMove = (e) => {
                const rect = wrapper.getBoundingClientRect();
                const relativeY = e.clientY - rect.top;
                const wrapperHeight = rect.height;
                const bottomThreshold = wrapperHeight * 0.4;
                this.showControls = relativeY >= bottomThreshold;
            };
            this.handleHoverLeave = () => {
                this.showControls = false;
            };
            wrapper.addEventListener('mousemove', this.handleHoverMove);
            wrapper.addEventListener('mouseleave', this.handleHoverLeave);
        },
        removeHoverListener() {
            const wrapper = this.$el;
            if (!wrapper) {
                return;
            }
            if (this.handleHoverMove) {
                wrapper.removeEventListener('mousemove', this.handleHoverMove);
            }
            if (this.handleHoverLeave) {
                wrapper.removeEventListener('mouseleave', this.handleHoverLeave);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.pdf-viewer-wrapper {
    position: relative;
    height: 100%;
    width: 100%;
    isolation: isolate;
}

.pdf-viewer {
    background: var(--colors-common-background-app);
    height: 100%;
    overflow-y: auto;
    overflow-x: auto;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-3);
    padding: var(--spacing-4);
    align-items: flex-start;
    will-change: scroll-position;

    &__page-container {
        position: relative;
        margin: 0 auto;
    }

    &__page {
        border-radius: var(--spacing-1);
        display: block;
        transform-origin: center top;
        contain: layout style paint;
    }

    &__expired-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
        z-index: 10;
    }

    &__expired-text {
        font-size: 167px;
        line-height: 145%;
        color: rgba(138, 138, 138, 0.15);
        text-transform: uppercase;
        transform: rotate(-45deg);
        user-select: none;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: clip;
    }
}
</style>
